// Home: Daily challenge card — one short question a day (src/core/daily.js). No XP; solving it
// keeps the day streak in "Your week" going. Answers are stored in the learner store (`daily`).
import { h } from './h.js';
import { dailyChallenge, dayKey, recordAttempt, solvedDays, DAILY_KEY } from '../core/daily.js';
import { play } from './sound.js';

const LETTERS = ['A', 'B', 'C', 'D'];

/** Consecutive days with a solved challenge, ending today (or yesterday if today is still open). */
export function dailyStreak(data, now = Date.now()) {
  const days = new Set(solvedDays(data).map((d) => d.key));
  let n = 0; const d = new Date(now);
  if (!days.has(dayKey(d.getTime()))) d.setDate(d.getDate() - 1);
  while (days.has(dayKey(d.getTime()))) { n++; d.setDate(d.getDate() - 1); }
  return n;
}

export function dailyCard({ store, now = Date.now() }) {
  const key = dayKey(now);
  const c = dailyChallenge(key);
  const card = h('section', { class: 'rz-daily panel', 'data-testid': 'home-daily', 'aria-labelledby': 'rz-daily-title' });
  let picked = null;
  const draw = () => {
    const data = store.get(DAILY_KEY, {}) || {};
    const today = data[key] || {};
    const streak = dailyStreak(data, now);
    const solved = !!today.done;
    const feedback = solved
      ? h('div', { class: 'rz-daily-fb ok', role: 'status', 'data-testid': 'daily-feedback' },
        h('b', {}, today.firstTry ? 'Correct — first try! ' : 'Solved! '), c.explain)
      : picked !== null ? h('div', { class: 'rz-daily-fb bad', role: 'status', 'data-testid': 'daily-feedback' }, h('b', {}, 'Not quite. '), 'Have another look and try again.') : null;
    // (replaceChildren would print a null child as the text "null", so drop empty parts first)
    card.replaceChildren(...[
      h('div', { class: 'rz-daily-head' },
        h('div', {}, h('div', { class: 'eyebrow' }, 'DAILY CHALLENGE · ABOUT 1 MIN'), h('h2', { id: 'rz-daily-title' }, solved ? 'Done for today' : 'Today’s question')),
        h('span', { class: `rz-daily-streak${streak ? ' on' : ''}`, 'data-testid': 'daily-streak', title: 'Days in a row with the daily challenge solved' }, streak ? `🔥 ${streak}-day streak` : 'Start a streak')),
      h('p', { class: 'rz-daily-q', 'data-testid': 'daily-question' }, c.prompt),
      h('div', { class: 'rz-daily-opts', role: 'group', 'aria-label': 'Answers' }, c.options.map((o, i) => {
        const state = solved && i === c.answer ? 'right' : picked === i && !solved ? 'wrong' : '';
        return h('button', {
          type: 'button', class: `rz-daily-opt ${state}`, 'data-testid': `daily-option-${i}`, disabled: solved ? true : null,
          'aria-pressed': picked === i ? 'true' : 'false',
          onclick: () => {
            picked = i;
            const ok = i === c.answer;
            store.set(DAILY_KEY, recordAttempt(store.get(DAILY_KEY, {}), key, ok, Date.now()));
            play(ok ? 'chime' : 'wrong');
            draw();
            card.querySelector('[data-testid="daily-feedback"]')?.focus?.();
          },
        }, h('span', { class: 'rz-daily-letter', 'aria-hidden': 'true' }, LETTERS[i]), h('span', {}, o));
      })),
      feedback,
      h('p', { class: 'rz-daily-note muted small' }, solved ? 'A new question appears tomorrow. Daily challenges keep your streak going; XP comes from missions and tickets.' : 'One question a day. Solve it to keep your streak going.'),
    ].filter(Boolean));
  };
  draw();
  return card;
}
